import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { MagnifyingGlass, Bookmark, CreditCard, SignOut, Lightning, ShieldCheck, UsersThree, UsersFour, ChartLineUp, EnvelopeSimple, Buildings, CaretDown, Gear, Tray, UserCircle, Storefront, EyeSlash } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const initials = (name, email) => {
  const src = (name || email || "").trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const orgType = user?.entitlements?.org_type || user?.organization?.org_type || "ESN";
  const isFinalClient = orgType === "FINAL_CLIENT";

  const esnNav = [
    { to: "/app", label: "Recherche", icon: MagnifyingGlass, testid: "nav-search" },
    { to: "/app/consultants", label: "Consultants", icon: UsersThree, testid: "nav-consultants" },
    { to: "/app/marketplace", label: "Marketplace", icon: Storefront, testid: "nav-marketplace" },
    { to: "/app/tracking", label: "Suivi", icon: ChartLineUp, testid: "nav-tracking" },
    { to: "/app/messagerie", label: "Messagerie", icon: Tray, testid: "nav-messagerie" },
    { to: "/app/team", label: "Équipe", icon: UsersFour, testid: "nav-team" },
    { to: "/app/saved", label: "Recherches", icon: Bookmark, testid: "nav-saved" },
    { to: "/app/hidden-signals", label: "Masqués", icon: EyeSlash, testid: "nav-hidden" },
    { to: "/app/billing", label: "Abonnement", icon: CreditCard, testid: "nav-billing" },
  ];
  const finalClientNav = [
    { to: "/app/marketplace", label: "Marketplace", icon: Storefront, testid: "nav-marketplace" },
    { to: "/app/team", label: "Équipe", icon: UsersFour, testid: "nav-team" },
  ];
  const nav = isFinalClient ? finalClientNav : esnNav;

  return (
    <header className="sticky top-0 z-40 glass border-b border-border">
      <div className="max-w-[1400px] mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/app" className="flex items-center gap-2" data-testid="header-logo-link">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => {
              const active = pathname === n.to || (n.to !== "/app" && pathname.startsWith(n.to + "/"));
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm hover-lift ${
                    active ? "bg-surface-2 text-fg" : "text-muted hover:text-fg"
                  }`}
                >
                  <Icon size={16} weight={active ? "fill" : "regular"} />
                  {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin"
              className="hidden sm:flex items-center gap-1.5 text-xs font-mono text-accent border border-accent/30 rounded-full px-3 py-1 hover-lift">
              <ShieldCheck size={12} weight="fill" /> ADMINISTRATION
            </Link>
          )}
          {user && user.entitlements?.is_paid === false && !isFinalClient && (
            <span className="hidden sm:flex items-center gap-1.5 text-xs font-mono text-accent border border-accent/30 rounded-full px-3 py-1 cursor-pointer hover-lift" data-testid="subscribe-badge" onClick={() => navigate("/app/billing")}>
              <Lightning size={12} weight="fill" /> S'ABONNER — 29 €/MOIS
            </span>
          )}
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger"
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-surface-2 transition-colors">
                <span className="w-8 h-8 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center font-display font-bold text-accent text-xs" data-testid="user-menu-avatar">
                  {initials(user?.name, user?.email)}
                </span>
                <span className="text-sm text-fg leading-tight hidden sm:block max-w-[140px] truncate" data-testid="header-user-name">{user?.name}</span>
                <CaretDown size={12} className="text-muted hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-surface border-border" data-testid="user-menu">
              <DropdownMenuLabel className="flex flex-col">
                <span className="text-fg font-semibold truncate">{user?.name}</span>
                <span className="text-xs text-muted font-normal truncate">{user?.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings" className="cursor-pointer gap-2"><Gear size={15} /> Réglages</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/app/settings/profile")} data-testid="menu-profile" className="cursor-pointer gap-2"><UserCircle size={15} /> Mon profil</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/app/settings/email")} data-testid="menu-email" className="cursor-pointer gap-2"><EnvelopeSimple size={15} /> Email & Signature</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/app/settings/organization")} data-testid="menu-org" className="cursor-pointer gap-2"><Buildings size={15} /> Organisation</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/app/settings/security")} data-testid="menu-security" className="cursor-pointer gap-2"><ShieldCheck size={15} /> Sécurité</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="cursor-pointer gap-2 text-destructive focus:text-destructive"><SignOut size={15} /> Déconnexion</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
